import { useState, useRef, useEffect } from "react";
import { useToken, useCurrentUser } from "../utils";
import { useLocation, Link } from "react-router-dom";
import { useDarkMode } from "../App";
import React from "react";
import { useQuery } from '@apollo/client';
import getUserByIdQuery from '../graphql/query/getUserById.query';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { performLogout } from "../lib/appLogout";
import styles from './Header.module.css';

export default function Header() {
  const { removeToken } = useToken();
  const location = useLocation();
  const { darkMode, setDarkMode } = useDarkMode();
  const currentUser = useCurrentUser();
  
  const { data: userData } = useQuery(getUserByIdQuery, {
    variables: { userId: currentUser?.id },
    skip: !currentUser?.id,
  });
  
  const userProfile = userData?.userById;
  const firstName = userProfile?.firstname || 'User';
  const firstNameInitial = firstName.charAt(0).toUpperCase();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = () => {
    void performLogout(removeToken);
  };

  const handleMouseEnter = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setIsMenuOpen(true);
  };

  const handleMouseLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === "/") {
      return (
        location.pathname === "/" ||
        location.pathname.startsWith("/search") ||
        location.pathname.startsWith("/product")
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { path: '/', label: 'Browse', icon: <HomeIcon /> },
    { path: '/messages', label: 'Messages', icon: <ChatBubbleOutlineIcon /> },
    { path: '/mylistings', label: 'My Listings', icon: <FormatListBulletedIcon /> },
    { path: '/about', label: 'About', icon: <InfoOutlinedIcon /> },
  ];

  return (
    <header className={`${styles.header} ${darkMode ? styles.dark : ''}`}>
      <div className={styles.headerAccent} aria-hidden />
      <div className={styles.headerContent}>
        <Link to="/" className={styles.logo}>
          <span className={styles.logoMark} aria-hidden>
            <DirectionsCarIcon fontSize="small" />
          </span>
          <span className={styles.logoText}>Drive Now!</span>
        </Link>

        <nav className={styles.desktopNav} aria-label="Primary">
          <div className={styles.navTray}>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`${styles.navLink} ${isActive(item.path) ? styles.navLinkActive : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <Link
            to="/newlisting"
            className={`${styles.sellCta} ${isActive('/newlisting') ? styles.sellCtaActive : ''}`}
          >
            <AddCircleOutlineIcon aria-hidden />
            Sell Car
          </Link>
        </nav>

        <div className={styles.actions}>
          <button
            className={styles.iconButton}
            onClick={() => setDarkMode(prev => !prev)}
            aria-label="Toggle dark mode"
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </button>

          <div
            ref={menuRef}
            className={styles.userMenuContainer}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className={styles.userButton} type="button" aria-haspopup="menu">
              <div className={styles.avatar}>
                {firstNameInitial}
              </div>
              <span className={styles.userName}>
                {firstName}
              </span>
            </button>

            <div className={`${styles.dropdown} ${isMenuOpen ? styles.dropdownOpen : ''}`}>
              <div className={styles.dropdownHeader}>
                <div className={styles.dropdownAvatar}>
                  {firstNameInitial}
                </div>
                <div className={styles.dropdownUserInfo}>
                  <span className={styles.dropdownName}>
                    {firstName}
                  </span>
                  <span className={styles.dropdownEmail}>
                    {userProfile?.email || 'Account'}
                  </span>
                </div>
              </div>
              
              <div className={styles.dropdownDivider} />
              
              <Link to="/profile" className={styles.dropdownItem} onClick={() => setIsMenuOpen(false)}>
                <PersonOutlineIcon />
                <span>Profile</span>
              </Link>
              <Link to="/mylistings" className={styles.dropdownItem} onClick={() => setIsMenuOpen(false)}>
                <FormatListBulletedIcon />
                <span>My Listings</span>
              </Link>
              <Link to="/newlisting" className={styles.dropdownItem} onClick={() => setIsMenuOpen(false)}>
                <AddCircleOutlineIcon />
                <span>Create Listing</span>
              </Link>
              
              <div className={styles.dropdownDivider} />
              
              <button className={styles.dropdownItem} onClick={handleLogout} data-testid="header-logout">
                <LogoutIcon />
                <span>Sign Out</span>
              </button>
            </div>
          </div>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      <div className={`${styles.mobileNav} ${isMobileMenuOpen ? styles.mobileNavOpen : ''}`}>
        <div className={styles.mobileNavContent}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.mobileNavLinkActive : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}

          <Link
            to="/newlisting"
            className={`${styles.mobileNavLink} ${styles.mobileSellCta} ${isActive('/newlisting') ? styles.mobileNavLinkActive : ''}`}
          >
            <AddCircleOutlineIcon />
            <span>Sell Car</span>
          </Link>
          
          <div className={styles.mobileNavDivider} />
          
          <Link to="/profile" className={styles.mobileNavLink}>
            <PersonOutlineIcon />
            <span>Profile</span>
          </Link>
          
          <button className={styles.mobileNavLink} onClick={handleLogout}>
            <LogoutIcon />
            <span>Sign Out</span>
          </button>
          
          <div className={styles.mobileNavDivider} />
          
          <button 
            className={styles.mobileNavLink}
            onClick={() => setDarkMode(prev => !prev)}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
